// src/components/ClosedSignalsTicker.js
import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { db } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';

/* ---- helpers ---- */
function fmtPrice(val) {
  if (val == null || val === '') return '-';
  const n = Number(val);
  if (isNaN(n)) return '-';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(4);
  if (n >= 0.0001) return n.toFixed(6);
  return n.toPrecision(4);
}

function calcGain(sig) {
  const entry = parseFloat(sig.entryPrice || sig.averageEntryPrice);
  const close = parseFloat(sig.closePrice);
  if (!entry || !close || isNaN(entry) || isNaN(close)) return null;
  const isShort = (sig.signalType || '').toUpperCase() === 'SHORT';
  return isShort ? ((entry - close) / entry) * 100 : ((close - entry) / entry) * 100;
}

function getResultLabel(status = '') {
  const s = status.toUpperCase();
  if (s.includes('BOTH')) return { label: 'BOTH TP HIT', win: true, type: 'tp2' };
  if (s.includes('TP2')) return { label: 'TP2 HIT', win: true, type: 'tp2' };
  if (s.includes('TP1')) return { label: 'TP1 HIT', win: true, type: 'tp1' };
  if (s.includes('SL') || s.includes('STOP')) return { label: 'SL HIT', win: false, type: 'sl' };
  if (s.includes('INVALID')) return { label: 'INVALID', win: null, type: null };
  return { label: status || 'CLOSED', win: null, type: null };
}

function getHitPrice(sig, resultType) {
  if (resultType === 'tp2') return { label: 'TP2', price: sig.takeProfit2 };
  if (resultType === 'tp1') return { label: 'TP1', price: sig.takeProfit };
  if (resultType === 'sl') return { label: 'SL', price: sig.stopLoss };
  return null;
}

/* ---- component ---- */
const ClosedSignalsTicker = () => {
  const [signals, setSignals] = useState([]);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'signals'), orderBy('closedAt', 'desc'), limit(40));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setSignals(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => {}
    );
    return () => unsub();
  }, []);

  if (signals.length === 0) return null;

  const items = [...signals, ...signals];

  return (
    <TickerBar onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <TickerLabel>
        <LabelDot />
        Signal Feed
      </TickerLabel>

      <TickerClip>
        <TickerTrack ref={trackRef} paused={paused} count={signals.length}>
          {items.map((sig, idx) => {
            const gain = calcGain(sig);
            const result = getResultLabel(sig.status || '');
            const hitPrice = getHitPrice(sig, result.type);
            const isLong = (sig.signalType || '').toUpperCase() !== 'SHORT';

            return (
              <React.Fragment key={`${sig.id}-${idx}`}>
                <TickerItem>
                  <Coin>{(sig.symbol || sig.coinId || '?').toUpperCase()}</Coin>
                  <DirBadge long={isLong}>{isLong ? 'LONG' : 'SHORT'}</DirBadge>

                  <PriceGroup>
                    <EntryLabel>Entry</EntryLabel>
                    <EntryVal>{fmtPrice(sig.entryPrice || sig.averageEntryPrice)}</EntryVal>
                  </PriceGroup>

                  <Arrow win={gain > 0} loss={gain < 0}>
                    {gain > 0 ? '↑' : gain < 0 ? '↓' : '→'}
                  </Arrow>

                  {hitPrice && (
                    <PriceGroup>
                      <HitLabel win={result.win === true} loss={result.win === false}>
                        {hitPrice.label}
                      </HitLabel>
                      <HitVal win={result.win === true} loss={result.win === false}>
                        {fmtPrice(hitPrice.price)}
                      </HitVal>
                    </PriceGroup>
                  )}

                  {gain !== null && (
                    <GainVal win={gain > 0} loss={gain < 0}>
                      {gain > 0 ? '+' : ''}
                      {gain.toFixed(2)}%
                    </GainVal>
                  )}

                  <ResultChip win={result.win === true} loss={result.win === false} neutral={result.win === null}>
                    {result.label}
                  </ResultChip>
                </TickerItem>
                {idx < items.length - 1 && (
                  <SeparatorWrap>
                    <SeparatorLogo src="/logo.png" alt="Trend Genie" />
                  </SeparatorWrap>
                )}
              </React.Fragment>
            );
          })}
        </TickerTrack>
      </TickerClip>
    </TickerBar>
  );
};

export default ClosedSignalsTicker;

/* ============================================================
   KEYFRAMES
============================================================ */
const marquee = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

/* ============================================================
   STYLES
============================================================ */
const TickerBar = styled.div`
  width: 100%;
  background: var(--bg-surface);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  overflow: hidden;
  height: 44px;
  position: relative;
`;

const TickerLabel = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1rem 0 1.2rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-muted);
  border-right: 1px solid var(--border-color);
  height: 100%;
  background: var(--bg-secondary);
  white-space: nowrap;
`;

const LabelDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const TickerClip = styled.div`
  flex: 1;
  overflow: hidden;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 48px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-surface), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-surface), transparent);
  }
`;

const TickerTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  white-space: nowrap;
  will-change: transform;
  animation: ${marquee} ${(p) => Math.max(p.count * 3.5, 30)}s linear infinite;
  animation-play-state: ${(p) => (p.paused ? 'paused' : 'running')};
`;

const TickerItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0 0.9rem;
`;

const SeparatorWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
`;

const Coin = styled.span`
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--text-color);
  letter-spacing: -0.2px;
`;

const DirBadge = styled.span`
  font-size: 0.6rem;
  font-weight: 800;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  letter-spacing: 0.05em;
  background: ${(p) => (p.long ? 'rgba(41,171,226,0.12)' : 'rgba(239,68,68,0.1)')};
  color: ${(p) => (p.long ? '#29ABE2' : '#ef4444')};
  border: 1px solid ${(p) => (p.long ? 'rgba(41,171,226,0.25)' : 'rgba(239,68,68,0.2)')};
`;

const PriceGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const EntryLabel = styled.span`
  font-size: 0.68rem;
  color: var(--text-muted);
`;

const EntryVal = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary-color);
`;

const Arrow = styled.span`
  font-size: 0.7rem;
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-muted)')};
  flex-shrink: 0;
`;

const HitLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-muted)')};
`;

const HitVal = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-secondary-color)')};
`;

const GainVal = styled.span`
  font-size: 0.78rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-muted)')};
`;

const ResultChip = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: ${(p) =>
    p.win ? 'rgba(16,185,129,0.12)' : p.loss ? 'rgba(239,68,68,0.1)' : 'rgba(107,114,128,0.1)'};
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : '#9ca3af')};
`;

const SeparatorLogo = styled.img`
  display: block;
  align-self: center;
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.95;
`;
